// App.tsx
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useParams,
  useLocation,
} from 'react-router-dom';

import { ScrollToTop } from './components/common/ScrollToTop';
import { MenuModal } from './components/common/MenuModal';

// 各ページのインポート
import { RoomList } from './pages/admin/property/RoomList';
import { AdminRoomEdit } from './pages/admin/property/room/adminEdit';
import { Contract } from './pages/admin/contract';
import { AdminEditProperty } from './pages/admin/property/edit';
import { AdminMagazinePage } from './pages/admin/magazine';
import { AdminLoginPage } from './pages/admin';
import { Property } from './pages/admin/property';
import { AdminNews } from './pages/admin/news';
import { MonthlyPage } from './pages/monthly';
import { MonthlyDetail } from './pages/monthly/[id]/detail';
import { HotelPage } from './pages/hotel';
import { HotelPropertiesIndex } from './pages/hotel/properties';
import { HotelDetail } from './pages/hotel/[id]/detail';
import { NotFound } from './pages/errors/NotFound';
import { MagazinePage } from './pages/magazine';
import { MagazineDetail } from './pages/magazine/[id]/detail';
import { MagazineShopDetail } from './pages/admin/magazine/MagazineShopDetail';
import { MagazineShopCreate } from './components/Magazine/MagazineShopCreate';
import { Policy } from './pages/policy';
import { ContactIndex } from './pages/inquiry';
import { ApplyFormContainer } from './pages/monthly/ApplyFormContainer';
import { ProtectedAdminRoute } from './components/admin/Common/ProtectedAdminRoute';

// 言語検出のカスタムフックをインポート
import { useLanguageDetection } from './useLanguageDetection';

// Layout コンポーネント：URLから言語コードを取得して利用可能（例：i18nの設定など）
const Layout = () => {
  const { lang } = useParams();
  // 例: i18n.changeLanguage(lang);
  return (
    <>
      <ScrollToTop />
      <MenuModal />
      <Outlet />
    </>
  );
};

// 一般ユーザー向けのルート（URLの最初に言語コードが付く）
const PublicRoutes = () => (
  <Route path="/:lang" element={<Layout />}>
    <Route path="monthly" element={<MonthlyPage />} />
    <Route path="monthly/:propertyId" element={<MonthlyDetail />} />
    <Route
      path="monthly/:property_id/apply/:room_id"
      element={<ApplyFormContainer />}
    />
    <Route path="hotel" element={<HotelPage />} />
    <Route path="hotel/properties" element={<HotelPropertiesIndex />} />
    <Route path="hotel/:property_id" element={<HotelDetail />} />
    <Route path="magazine" element={<MagazinePage />} />
    <Route path="magazine/:magazine_id" element={<MagazineDetail />} />
    <Route path="policy" element={<Policy />} />
    <Route path="inquiry" element={<ContactIndex />} />
  </Route>
);

// 管理者向けのルート（言語コード不要の場合）
const AdminRoutes = () => (
  <>
    <Route path="/admin/login" element={<AdminLoginPage />} />
    <Route path="/admin" element={<ProtectedAdminRoute />}>
      <Route path="property" element={<Property />} />
      <Route path="property/:propertyId/edit" element={<AdminEditProperty />} />
      <Route path="property/:propertyId/room" element={<RoomList />} />
      <Route
        path="property/:propertyId/room/:roomId/edit"
        element={<AdminRoomEdit />}
      />
      <Route path="news" element={<AdminNews />} />
      <Route path="contract" element={<Contract />} />
      <Route path="magazine" element={<AdminMagazinePage />} />
      <Route path="magazine/shop/new" element={<MagazineShopCreate />} />
      <Route path="magazine/shop/:shop_id" element={<MagazineShopDetail />} />
    </Route>
  </>
);

/**
 * URL に /ja または /jp が含まれていれば body に lang-ja クラスを付与する
 */
const CheckLang = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.toLowerCase();
    if (path.includes('/ja') || path.includes('/jp')) {
      document.body.classList.add('lang-ja');
    } else {
      document.body.classList.remove('lang-ja');
    }
  }, [location]);

  // 何も描画しない
  return null;
};

export const App = () => {
  // ページ内の .text-base 要素に対して自動的に lang 属性を付与する
  useLanguageDetection();

  return (
    <>
      <Helmet>
        <link
          rel="icon"
          type="image/png"
          href={process.env.PUBLIC_URL + '/favicon.png'}
          sizes="32x32"
        />
      </Helmet>
      <Router>
        {/* URLを監視して body クラスを切り替える */}
        <CheckLang />

        <Routes>
          {/* 管理者向けルート */}
          {AdminRoutes()}

          {/* 一般ページ：URL の最初に言語コードが付く */}
          {PublicRoutes()}

          {/* 存在しないルートまたは言語コードがない場合は、デフォルトとして "/en/monthly" へリダイレクト */}
          <Route path="*" element={<Navigate to="/en/monthly" replace />} />
        </Routes>
      </Router>
    </>
  );
};

export default App;
